import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useQuery, useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient } from "../services/api";
import { FileText, CalendarDays, Calendar, ArrowRight, Clock, CheckCircle, XCircle, Loader2, AlertCircle, TrendingUp, Heart } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const statusStyles = {
  pending:   { backgroundColor: "rgba(240,165,0,0.12)",  color: "#f0a500",  border: "1px solid rgba(240,165,0,0.25)"  },
  approved:  { backgroundColor: "rgba(39,166,68,0.12)",  color: "#27a644",  border: "1px solid rgba(39,166,68,0.25)"  },
  rejected:  { backgroundColor: "rgba(229,72,77,0.12)",  color: "#e5484d",  border: "1px solid rgba(229,72,77,0.25)"  },
  cancelled: { backgroundColor: "rgba(98,102,109,0.12)", color: "#8a8f98",  border: "1px solid rgba(98,102,109,0.25)" },
};

const statusIcons = {
  pending:   <Clock className="w-3.5 h-3.5" />,
  approved:  <CheckCircle className="w-3.5 h-3.5" />,
  rejected:  <XCircle className="w-3.5 h-3.5" />,
  cancelled: <XCircle className="w-3.5 h-3.5" />,
};

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const PortalDashboard = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // Step 1: resolve employee ID (required for holiday lookup)
  const employeeQ = useQuery({
    queryKey: ['portal-employee-me'],
    queryFn: async () => {
      try {
        const r = await apiClient.get("v1/employees/me");
        return r.data;
      } catch {
        return null;
      }
    },
  });
  const employeeId = employeeQ.data?.id ?? null;

  // Step 2: load portal data in parallel
  const [absencesQ, holidaysQ, payslipQ, balancesQ, wellbeingQ] = useQueries({
    queries: [
      {
        queryKey: ['portal-absences'],
        queryFn: async () => {
          const res = await apiClient.get("v1/absences/me?limit=20");
          const all = res.data?.items ?? res.data ?? [];
          return all
            .filter(a => ["pending", "approved"].includes(a.status))
            .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
            .slice(0, 5);
        },
      },
      {
        queryKey: ['portal-upcoming-holidays', employeeId],
        queryFn: async () => {
          const calRes = await apiClient.get(`v1/holidays/employee-calendars/employee/${employeeId}`);
          const upcoming = calRes.data?.upcoming_holidays ?? calRes.data?.holidays ?? [];
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return upcoming
            .filter(h => new Date(h.date) >= today)
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .slice(0, 3);
        },
        enabled: employeeQ.isSuccess && !!employeeId,
      },
      {
        queryKey: ['portal-last-payslip'],
        queryFn: async () => {
          const res = await apiClient.get("v1/payroll/my-entries?limit=1");
          const entries = res.data?.items ?? res.data ?? [];
          return entries.length > 0 ? entries[0] : null;
        },
      },
      {
        queryKey: ['portal-leave-balances'],
        queryFn: async () => {
          const res = await apiClient.get("v1/leave-policies/balances/me").catch(() => ({ data: [] }));
          return res.data ?? [];
        },
      },
      {
        queryKey: ['portal-wellbeing'],
        queryFn: async () => {
          const res = await apiClient.get("v1/wellbeing/burnout/me").catch(() => null);
          return res?.data ?? null;
        },
      },
    ],
  });

  const absences = absencesQ.data ?? [];
  const holidays = holidaysQ.data ?? [];
  const lastPayslip = payslipQ.data ?? null;
  const leaveBalances = (balancesQ.data ?? []).slice(0, 3);
  const wellbeing = wellbeingQ.data ?? null;
  const loading = employeeQ.isPending || absencesQ.isPending || payslipQ.isPending;

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t("portal.dashboard.goodMorning");
    if (hour < 18) return t("portal.dashboard.goodAfternoon");
    return t("portal.dashboard.goodEvening");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#5e6ad2" }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {greeting()}, {user?.first_name} 👋
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t("portal.dashboard.subtitle")}
        </p>
      </div>

      {/* 5 widgets in 2 rows */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Widget 1 — Upcoming absences */}
        <Card className="flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarDays className="w-5 h-5" style={{ color: "#5e6ad2" }} />
              {t("portal.dashboard.absences")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 space-y-2">
            {absences.length === 0 ? (
              <p className="text-sm text-gray-400 py-2">
                {t("portal.dashboard.noAbsences")}
              </p>
            ) : (
              absences.map((a) => (
                <div key={a.id} className="flex items-start gap-2">
                  <span className="mt-0.5 flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium" style={statusStyles[a.status] ?? statusStyles.pending}>
                    {statusIcons[a.status]}
                    {a.status}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {a.absence_type_label ?? a.absence_type ?? t("portal.dashboard.absence")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(a.start_date)} → {formatDate(a.end_date)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </CardContent>
          <div className="px-6 pb-4">
            <Link
              to="/portal/mon-espace"
              className="inline-flex items-center gap-1 text-sm font-medium hover:underline"
              style={{ color: "#5e6ad2" }}
            >
              {t("portal.dashboard.viewAll")} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Card>

        {/* Widget 2 — Next public holidays */}
        <Card className="flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="w-5 h-5" style={{ color: "#27a644" }} />
              {t("portal.dashboard.holidays")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 space-y-2">
            {holidays.length === 0 ? (
              <p className="text-sm text-gray-400 py-2">
                {t("portal.dashboard.noHolidays")}
              </p>
            ) : (
              holidays.map((h, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{h.name ?? h.name_fr ?? h.name_en ?? t("portal.fallback.holiday")}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(h.date)}</p>
                  </div>
                  <Badge variant="outline" className="text-xs ml-2 shrink-0">
                    {t("portal.dashboard.daysShort", { count: Math.ceil((new Date(h.date) - new Date()) / 86400000) })}
                  </Badge>
                </div>
              ))
            )}
          </CardContent>
          <div className="px-6 pb-4">
            <Link
              to="/portal/mon-espace"
              className="inline-flex items-center gap-1 text-sm font-medium hover:underline"
              style={{ color: "#5e6ad2" }}
            >
              {t("portal.dashboard.viewAll")} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Card>

        {/* Widget 3 — Last payslip */}
        <Card className="flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="w-5 h-5" style={{ color: "#828fff" }} />
              {t("portal.dashboard.lastPayslip")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1">
            {lastPayslip ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{t("portal.dashboard.period")}</span>
                  <span className="text-sm font-medium text-foreground">
                    {lastPayslip.calculated_at
                      ? new Date(lastPayslip.calculated_at).toLocaleDateString(undefined, { month: "long", year: "numeric" })
                      : `P-${lastPayslip.period_id}`}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{t("portal.dashboard.netPay")}</span>
                  <span className="text-lg font-bold text-foreground">
                    {Number(lastPayslip.net_pay ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{t("portal.dashboard.grossPay")}</span>
                  <span className="text-sm text-foreground">
                    {Number(lastPayslip.gross_pay ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 py-2 text-gray-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                {t("portal.dashboard.noPayslip")}
              </div>
            )}
          </CardContent>
          <div className="px-6 pb-4">
            <Link
              to="/portal/mon-espace"
              className="inline-flex items-center gap-1 text-sm font-medium hover:underline"
              style={{ color: "#5e6ad2" }}
            >
              {t("portal.dashboard.viewBulletins")} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Card>

        {/* Widget 4 — Leave balances */}
        <Card className="flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="w-5 h-5" style={{ color: "#f0a500" }} />
              {t("portal.dashboard.leaveBalances")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 space-y-3">
            {leaveBalances.length === 0 ? (
              <p className="text-sm text-gray-400 py-2">{t("portal.dashboard.noBalances")}</p>
            ) : (
              leaveBalances.map((b, i) => {
                const used = b.used_days ?? 0;
                const max = b.max_balance ?? b.accrued_days ?? 1;
                const available = b.available_days ?? b.accrued_days ?? 0;
                const pct = Math.min(100, Math.round((used / (max || 1)) * 100));
                return (
                  <div key={i} className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="font-medium text-foreground truncate max-w-[70%]">{b.policy_name ?? b.name ?? t("portal.fallback.leave")}</span>
                      <span className="font-bold" style={{ color: available < 3 ? "#e5484d" : "#27a644" }}>
                        {t("portal.dashboard.daysShort", { count: available })}
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: available < 3 ? "#e5484d" : "#5e6ad2" }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
          <div className="px-6 pb-4">
            <Link
              to="/portal/mon-espace?tab=soldes"
              className="inline-flex items-center gap-1 text-sm font-medium hover:underline"
              style={{ color: "#5e6ad2" }}
            >
              {t("portal.dashboard.viewBalances")} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Card>

        {/* Widget 5 — Wellbeing */}
        <Card className="flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Heart className="w-5 h-5" style={{ color: "#e5484d" }} />
              {t("portal.dashboard.wellbeing")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1">
            {wellbeing ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground mb-0.5">{t("portal.dashboard.burnoutRisk")}</div>
                    <div className="text-2xl font-bold text-foreground">
                      {wellbeing.score}<span className="text-sm font-normal text-muted-foreground">/100</span>
                    </div>
                  </div>
                  <div className="text-4xl">
                    {wellbeing.level === "low" ? "💚" : wellbeing.level === "medium" ? "🟡" : "🔴"}
                  </div>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${wellbeing.score}%`,
                      backgroundColor: wellbeing.level === "low" ? "#27a644" : wellbeing.level === "medium" ? "#f0a500" : "#e5484d",
                    }}
                  />
                </div>
                <div className="text-xs text-muted-foreground">
                  {wellbeing.level === "low"
                    ? t("portal.dashboard.burnoutLow")
                    : wellbeing.level === "medium"
                    ? t("portal.dashboard.burnoutMedium")
                    : t("portal.dashboard.burnoutHigh")}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-4 gap-2 text-center">
                <Heart className="w-8 h-8 text-gray-300" />
                <p className="text-sm text-muted-foreground">{t("portal.dashboard.checkInPrompt")}</p>
              </div>
            )}
          </CardContent>
          <div className="px-6 pb-4">
            <Link
              to="/portal/mon-espace?tab=bienetre"
              className="inline-flex items-center gap-1 text-sm font-medium hover:underline"
              style={{ color: "#5e6ad2" }}
            >
              {t("portal.dashboard.myCheckIn")} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PortalDashboard;
